// ────────────────────────────────────────────────────────────────────────────
//  en.ts — English (UK) translations
//  Must mirror every key from de.ts exactly. TypeScript catches missing keys
//  via the Translation type.
// ────────────────────────────────────────────────────────────────────────────

import type { Translation } from './de'

export const en: Translation = {
  common: {
    save:        'Save',
    cancel:      'Cancel',
    delete:      'Delete',
    edit:        'Edit',
    change:      'Change',
    back:        'Back',
    loading:     'Loading…',
    saving:      'Saving…',
    sending:     'Sending…',
    saved:       '✓ Saved',
    error:       'Error',
    confirm:     'Confirm',
    close:       'Close',
    open:        'Open',
    optional:    'optional',
    required:    'required',
    yes:         'Yes',
    no:          'No',
    or:          'or',
    learnMore:   'Learn more',
  },

  auth: {
    login:           'Sign in',
    logout:          'Sign out',
    email:           'Email',
    password:        'Password',
    currentPassword: 'Current password',
    newPassword:     'New password',
    repeatPassword:  'Repeat password',
    appleSignIn:     'Sign in with Apple',
    appleSoon:       'Apple ID web login is being configured.',
    backToOverview:  '← Back to home',
    welcome:         'Welcome back',
    signInPrompt:    'Continue with your CustoSoft account.',
    orWithEmail:     'OR WITH EMAIL',
    forgotPassword:  'Forgotten your password?',
    noAccount:       'No account yet?',
    registerHint:    'You can register for free in the iOS or Mac app.',
    minLength:       'At least 8 characters.',
    notMatching:     'Passwords do not match.',
  },

  nav: {
    home:      'Home',
    dashboard: 'Overview',
    bug:       'Report a bug',
    profile:   'Profile',
    admin:     'Open admin',
    chat:      'Chat',
    punch:     'Time clock',
    files:     'Files',
    recruit:   'Recruitment',
    org:       'Org',
    shop:      'Shop',
    payroll:   'Time tracking',
    shifts:    'Shifts',
  },

  dashboard: {
    greetingNight:   'Good night',
    greetingMorning: 'Good morning',
    greetingDay:     'Good afternoon',
    greetingEvening: 'Good evening',
    welcomeBack:     'Welcome back to CustoSoft.',
    glance:          'At a glance',
    areas:           'Webapp sections',
    soonPill:        'SOON',
    statThisWeek:    'This week',
    statUnread:      'Unread',
    statRecruit:     'Applications',
    statStatus:      'Status',
    statAllOk:       'All systems running',
    statHintPunch:   'Time tracking coming soon',
    statHintChat:    'Chat coming soon',
    statHintRecruit: 'Recruitment coming soon',
    fullFunction:    'The full feature set is already available in our iOS and Mac apps.',
    rolloutNote:     'Webapp sections are being unlocked one by one.',
    bugBannerKicker: 'BETA TESTERS · DIRECT TO DEV',
    bugBannerTitle:  'Found a bug? Got an idea?',
    bugBannerText:   'Tell us right away — upload screenshots, videos, logs. Goes <strong>straight</strong> to our dev team with email notification.',
    wallClockTitle:  'Time clock',
    wallClockHint:   'Live timer — synced with your iOS app.',
    wallClockNotIn:  'Currently clocked out',
    wallClockIn:     'Clocked in',
    wallClockOpen:   'Open in the app',
    // Quick-Stats / Cards — were hard-coded inside Dashboard.tsx
    todayWorked:        'Today',
    weekTotal:          'This week',
    chatUnreadOne:      '{n} unread',
    chatUnreadOther:    '{n} unread',
    expiringOne:        '1 subscription is expiring soon',
    expiringOther:      '{n} subscriptions are expiring soon',
    expiresTomorrow:    'tomorrow',
    expiresInDays:      'in {n} days',
    timeYesterday:      'Yesterday',
    activeCountSuffix:  'active',
  },

  profile: {
    title:           'Profile & settings',
    subtitle:        'Manage your account, your appearance and your security.',
    accountTab:      'Account',
    securityTab:     'Security',
    appearanceTab:   'Appearance',
    languageTab:     'Language',
    subscriptionTab: 'Subscriptions',
    orgTab:          'Organisation',

    accountTitle:    'Account',
    accountSubtitle: 'Your name and avatar — visible to other org members.',
    firstName:       'First name',
    lastName:        'Last name',
    fullName:        'Display name',
    accountType:     'Account type',
    typePrivate:     '🙂 Private',
    typeOrg:         '🏢 Organisation',
    emailVerified:   '✓ Email verified',
    emailUnverified: '⚠ Email not verified',
    avatarChange:    '📷 Change avatar',
    avatarRemove:    'Remove',
    avatarRemoveQ:   'Really remove avatar?',

    appearanceTitle: 'Appearance',
    appearanceSub:   'Light, dark or follow the system.',
    themeLight:      'Light',
    themeDark:       'Dark',
    themeSystem:     'System',
    appearanceNote:  'In light mode CustoSoft gets a white liquid-glass design in iOS style.',

    languageTitle:   'Language',
    languageSub:     'Also for emails (verification, reset, bug confirmations).',

    securityTitle:   'Security',
    securitySub:     'Manage password and email address.',
    pwLabel:         'Password',
    pwLastChanged:   'Last changed: unknown',
    pwChangeTitle:   'Change password',
    pwOk:            '✓ Password changed.',
    emailAddress:    'Email address',
    newEmailLabel:   'New email address',
    sendCode:        'Send code',
    codeSentHint:    "We'll send a 6-digit code to the new address.",
    enterCode:       'Enter the code from the email',
    codeSentTo:      "We've sent a 6-digit code to {email}.",
    codeInvalid:     'Invalid code.',
    otherEmail:      'Different email',

    subsTitle:       'Active subscriptions & purchases',
    subsSub:         'Which extensions are active and where they were purchased.',
    subsNone:        "You haven't activated any extension yet.",
    subsBuy:         'Unlock in the iOS app →',
    subsExpiresAt:   'Expires on {date}',
    subsActiveSince: 'Active since {date}',
    subsViaApple:    '🍎 Apple',
    subsViaWeb:      '🌐 Web',
    subsViaOrg:      '🏢 Via organisation',
    subsCancelApple: 'Cancel via App Store',
    subsCancelWeb:   'Cancel',
    subsAppleHint:   'Apple subscriptions are managed in iOS Settings → Apple ID → Subscriptions.',

    orgTitle:        'Organisation',
    orgSub:          'Which company you belong to.',
    orgNone:         'Not assigned to any organisation.',
    orgRole:         'Role',
    orgMembers:      'Members',
    orgLeave:        'Leave organisation',
    orgLeaveQ:       'Really leave this organisation? You will lose access to org content.',

    deleteTitle:     '⚠ Delete account',
    deleteText:      'If you would like to delete your account, drop us a quick mail to {email}. We delete it GDPR-compliant including all of your content.',
  },

  bug: {
    kicker:        'BUG TRACKER · DIRECT TO DEV',
    title:         "Something not working?",
    sub:           'Describe it as precisely as possible — a screenshot or screen-recording helps us most. Goes <strong>straight</strong> to our dev team with email notification.',
    fieldTitle:    "What's wrong?",
    titlePlace:    'Short and snappy — e.g. "Clock-in button not responding"',
    fieldDesc:     'More detailed description',
    descPlace:     'What did you do? What happened? What should have happened? Reproducible?',
    severity:      'Severity',
    sevLow:        '🟢 Low — cosmetic',
    sevMid:        '🟡 Medium — annoying but workaround exists',
    sevHigh:       '🟠 High — feature broken',
    sevCrit:       '🔴 Critical — app unusable',
    platform:      'Platform',
    platIos:       'iPhone',
    platIpad:      'iPad',
    platMac:       'Mac',
    platWeb:       'Webapp (here)',
    platAll:       'All / multiple',
    appVersion:    'App version (optional)',
    appVerPlace:   '1.7 (Build 8)',
    attach:        'Attachments — screenshot, video, file (50 MB max per file)',
    dropZone:      'Drag a file here or click',
    dropTypes:     'Images · Videos · PDFs · Logs · Zips — up to 50 MB',
    submit:        '🐛 Send report',
    titleRequired: 'Title is required.',
    waitUploads:   'Please wait until uploads are finished.',
    fileTooBig:    '"{name}" is bigger than 50 MB.',
    reporterLabel: 'Reporter',
    uploaded:      '✓ Uploaded',
    uploading:     '⏳ Loading…',
    successTitle:  'Thank you!',
    successText:   "Your report landed with our dev team — we're on it. You won't get an automated reply, but we'll come back to you if we have questions.",
    another:       'Report another bug',
  },

  shop: {
    title:           'Shop · Extensions',
    sub:             'All packages & add-ons. Purchases go through the iOS app — securely with your Apple ID.',
    visualTitle:     'Live Activity',
    visualText:      'Dynamic Island, Lock-Screen banner, Mac menu bar — your day at a glance, even outside the app.',
    cardSuffix:      'iOS / Mac app',
    dashTileSub:     'Extensions & add-ons',
    productName: {
      PunchClock:          'Time Clock',
      TerminalMode:        'Wall Time Clock',
      Recruitment:         'Recruitment Manager',
      ShiftPlanner:        'Shift Planner',
      BusinessBasic:       'Business Basic',
      BusinessL:           'Business L',
      BusinessMAX:         'Business MAX',
      BusinessBasicYearly: 'Business Basic Yearly',
      BusinessLYearly:     'Business L Yearly',
      BusinessMAXYearly:   'Business MAX Yearly',
      MoreSpace:           'Extended Storage',
    },
    productDesc: {
      PunchClock:          'Time tracking for one person — €3.99/month. 1 month free trial only in the iOS or Mac app.',
      TerminalMode:        'Mount an iPad to the wall: every employee clocks in by PIN.',
      Recruitment:         'Create job links, collect applications.',
      ShiftPlanner:        'Plan working hours, assign shifts, auto-notify the team in chat.',
      BusinessBasic:       '10 employee slots · time clock · files · chat',
      BusinessL:           '50 employee slots · time clock, files & chat for the whole team',
      BusinessBasicYearly: '10 slots · up to 20 % discount on the yearly plan',
      BusinessLYearly:     '50 slots · up to 20 % discount on the yearly plan',
    },
    iosOnlyHint:     'Apple Pay on the web is coming soon. Until then: Card / SEPA / PayPal via Stripe, or via the iOS / Mac app.',
    activeBadge:     '✓ Active',
    monthly:         'Monthly',
    yearly:          'Yearly · up to 20 %',
    saveYearly:      'Save up to 20 % with the yearly plan',
    free:            'Free',
    perMonth:        '/month',
    perYear:         '/year',
    oneTime:         'One-time',
    sectionTeam:     'For Teams',
    sectionTeamSub:  'Employee slots, time clock, files and chat — all included.',
    sectionSolo:     'For Individuals',
    sectionSoloSub:  'Personal time tracking — try 1 month free, then cancel anytime.',
    launchSaleIndefinite: 'Launch sale · free for an indefinite period',  // legacy, unused
    iosTrialOnly:    '1 month free · iOS / Mac only',
    tryInApp:        'Try free in the app',
    badge: {
      popular:      'POPULAR',
      new:          'NEW',
      iosOnly:      'iOS ONLY',
      upTo20:       'UP TO 20%',
      upTo20Short:  'UP 20%',
    },
    sectionAddons:   'Add-Ons',
    sectionAddonsSub: 'Optional extensions for your business pack.',
    sectionAddonsLockedSub: 'Locked — requires at least Business Basic.',
    heroKicker:      'Extensions · Subscriptions · Add-Ons',
    heroSub:         'All packages at a glance. Web checkout via Card / SEPA / PayPal — Apple Pay is coming.',
    applePayTeaserTitle: 'Apple Pay on the web',
    applePayTeaserText:  'Express checkout with Apple Pay is on its way and will appear directly on every card.',
    applePaySoon:    'Coming soon',
    openAppStore:    'App Store',
    appStoreKicker:  'Download on the',
    lockedBadge:     'Locked',
    webCheckoutSoon:      'Web checkout · coming soon',
    webCheckoutSoonShort: 'Web checkout coming soon',
    comingSoonTitle:      'Coming soon: pay right in the browser',
    comingSoonText:       'We\'re polishing the web checkout with Apple Pay, card, SEPA and PayPal. In the meantime grab it from the App Store — same account, same subscription, all in sync.',
    addonNeedsBusiness: 'Business Basic required',
    addonNeedsBusinessHint: 'Add-Ons only work in combination with a team pack.',
    orVia:           'or',
    liveDemo:        'Live demo',
    demoCaption:     'Live timer, in sync with your app',
    checkoutPay:     'Pay with Card / SEPA',
    checkoutManage:  'Manage subscription',
    checkoutLoading: 'Redirecting to Stripe…',
    checkoutNoUrl:   'Stripe did not return a checkout URL.',
    checkoutError:   'Checkout failed.',
    trustPrivacy:    { title: 'GDPR-grade privacy', text: 'Servers in the EU, payment data lives at Stripe — we never store your card.' },
    trustNoLockIn:   { title: 'No lock-in',          text: 'Monthly plans cancel anytime, yearly simply expires. No prepay traps.' },
    trustUpdates:    { title: 'Updates included',    text: 'Every new feature in your tier — automatically, no extra charge.' },
    trustApple:      { title: 'Apple-grade',         text: 'iOS / iPadOS / macOS native, with Apple Sign-In, Push and Live Activities.' },
    sectionSoonWeb:  'Soon: Web checkout',
    buyOnApple:      ' Buy in the app',
    openApp:         ' Open app',
    appStoreBadge:   'On the App Store',
    seeFeatures:     'See all features',
    teaserWebShop:   'The shop is currently iOS-only. Stripe checkout for the web is in preparation — as an org owner, you will then be able to pay directly in the browser.',
    backToTop:       'Back to top',
    badgeBest:       'POPULAR',
    badgeRecommended: 'RECOMMENDED',
    badgeFree:       'FREE',
    feat: {
      employees:      '{n} employee slots',
      punchclock:     'Time clock for 1 person',
      teamPunchclock: 'Time clock for the whole team',
      files:          'Employee files',
      chat:           'Team chat',
      recruitment:    'Collect & manage applications',
      terminal:       'Wall time clock (iPad kiosk)',
      cancelable:     'Cancel anytime',
      yearlyDiscount: 'up to 20 % discount versus monthly',
      privateOnly:    'Full features — even without an organisation',
      pinAuth:        'PIN login per employee',
      kiosk:          'Fullscreen mode',
      jobLinks:       'Public job links',
      attachments:    'CV & attachments',
      payroll:        'Time tracking & analysis',
      orgCreate:      'Create your own organisation',
      moreSpace:      'Extended storage (R2)',
      shiftsCreate:   'Create & edit shifts',
      shiftsAssign:   'Assign employees, role per shift',
      shiftsChannel:  'Auto-notify in shift planner channel',
      shiftsView:     'Employees see their shifts on every device',
      monthlyReactivation: 'Free in the launch sale · re-activate every month in the shop',  // legacy, unused
      firstMonthFree:      'First month free · iOS / Mac only',
    },
    bizIncludes:      'Business includes:',

    // Buy / Hero (redesign)
    buy:                  'Unlock now',
    sectionTeamKicker:    'TEAMS',
    sectionSoloKicker:    'SOLO',
    sectionAddonsKicker:  'ADD-ONS',
    pillDsgvo:            'GDPR · EU hosting',
    pillApple:            'Apple-native',
    pillCancel:           'Cancel anytime',

    // ─── Filter tabs (new shop layout) ────────────────────────────────
    filterAll:           'All',
    filterSolo:          'Solo',
    filterTeams:         'Teams',
    filterAddons:        'Add-ons',
    filterCompare:       'Compare',

    // ─── App Store banner (large, top) ────────────────────────────────
    appStoreNoticeKicker: 'IOS APP ONLY',
    appStoreNoticeTitle:  'All purchases run through the iOS app',
    appStoreNoticeText:   'The 1-month-free Punch Clock trial runs through the App Store via your Apple ID. Web checkout (card / SEPA / PayPal) is coming soon.',

    // ─── PunchClock card notices ──────────────────────────────────────
    freeAppStoreOnly:    'iOS app only',
    activateInApp:       'Activate free in the app',

    // Spotlight (NEW product)
    spotlightKicker:      'NEW IN THE SHOP',
    spotlightSticker:     'NEW',
    spotlightPriceNote:   'one-time purchase · no recurring fees',
    spotlightFeat1:       'Drag-and-drop weekly plan on Mac & iPad',
    spotlightFeat2:       'Assign people, role, break, notes',
    spotlightFeat3:       'Auto-channel in chat — everyone sees changes instantly',
    spotlightFeat4:       'Employees see their plan on every Apple device',
    spotlightSoonNote:    'Coming soon — no purchase available yet',
    spotlightSoonShort:   'SOON',
    soon:                 'Soon',
    soonBadge:            'Coming soon',
    soonNote:             'Still polishing — release date will follow.',

    // Comparison strip
    compareKicker:        'COMPARE',
    compareTitle:         'What\'s included where?',
    compareCol: {
      feature: 'Feature',
      solo:    'Solo',
      basic:   'Basic',
      large:   'L',
    },
    cmp: {
      punch:    'Time clock',
      chat:     'Team chat',
      files:    'Employee files',
      payroll:  'Time tracking',
      recruit:  'Recruitment manager · add-on',
      terminal: 'Wall time clock · add-on',
      shifts:   'Shift planner · add-on',
      slots10:  '10 employee slots',
      slots50:  '50 employee slots',
    },
    compareFootnote:      'Add-ons can be unlocked separately — once active they apply to the whole organisation.',

    // FAQ
    faqKicker: 'FAQ',
    faqTitle:  'Frequently asked',
    faq1: { q: 'How do employee slots work?',                     a: 'Every member of your org occupies one slot. Business Basic comes with 10, Business L with 50. You can deactivate members at any time — the slot is freed instantly.' },
    faq2: { q: 'Can I cancel monthly?',                           a: 'Yes. Monthly subscriptions can be cancelled in the App Store / via your Apple account at the end of any cycle. Yearly plans simply expire — no auto-renew if you don\'t want it.' },
    faq3: { q: 'What happens to my data when I cancel?',          a: 'Your data is preserved for 30 days — export as CSV or reactivate the subscription anytime. After 30 days it\'s deleted in line with GDPR.' },
    faq4: { q: 'Do my employees need their own subscription?',    a: 'No. As soon as they\'re in your organisation they get all unlocked features automatically — time clock, chat, files, shifts. You pay, the team uses.' },
    faq5: { q: 'Why is web checkout still disabled?',              a: 'We\'re currently testing the Apple Pay integration. Until the live switch, purchases run smoothly through the iOS app — same account, instantly available.' },

    // Footer CTA
    footerKicker: 'READY?',
    footerTitle:  'Up and running in 2 minutes.',
    footerSub:    'Download the app, sign in with Apple, set up your org. Everything else is self-explanatory.',
  },

  org: {
    title:           'Your organisation',
    sub:             'Manage members, role and invitations.',
    youBelong:       'You belong to',
    role:            'Role',
    members:         'Members',
    membersOf:       '{n} of {limit} members',
    activeExt:       'Active extensions',
    leaveBtn:        'Leave organisation',
    leaveConfirm:    'Really leave "{name}"? You will lose access to all org content (chat, files, etc.).',

    tabOverview:     'Overview',
    tabMembers:      'Members',
    tabPermissions:  'Permissions',
    tabInvites:      'Invitations',
    tabSettings:     'Settings',
    permsOverview:   'Permissions overview',
    permsOverviewSub: 'View and edit rights of all members on one page.',
    permsExpand:     'Expand',
    permsCollapse:   'Collapse',

    promoteAdmin:    'Promote to admin',
    demoteAdmin:     'Revoke admin rights',
    removeMember:    'Remove from org',
    removeConfirm:   'Really remove {name} from the organisation?',
    permsTitle:      'Permissions',
    permsSub:        'What {name} is allowed to do in the organisation.',
    permClose:       'Close',
    permsSaved:      '✓ Permissions saved.',
    permLabels: {
      canManageMembers:          'Manage members',
      canManageInviteCodes:      'Manage invite codes',
      canCreateGroups:           'Create group chats',
      canManageFiles:            'Manage files',
      canInviteToChats:          'Invite to chats',
      canUseMoreSpace:           'Use extended storage',
      canViewSalaries:           'View salaries',
      canManageEmployeeProfiles: 'Manage employee profiles',
      canManageOrgStructure:     'Manage org structure',
      canUseRecruitment:         'Use recruitment manager',
      canManageRecruitment:      'Manage recruitment',
    },

    inviteCodesTitle: 'Invite codes',
    inviteCodesSub:   'Share a code so someone can join the org.',
    createCode:       'Create new code',
    codeMaxUses:      'Max uses',
    codeNoLimit:      'unlimited',
    codeExpires:      'Expires',
    codeNoExpiry:     'No expiry',
    codeExpiresAt:    'Expires on {date}',
    codeUsed:         'used {n}×',
    codeUsedOf:       '{n} / {max} uses',
    codeCopy:         'Copy',
    codeCopied:       '✓ Copied',
    codeShare:        'Share',
    codeDelete:       'Delete',
    codeDeleteConfirm: 'Really delete this invite code?',
    codeFormUses:     'Number of uses',
    codeFormUsesAny:  'Unlimited (∞)',
    codeFormUses1:    'Once',
    codeFormUsesN:    '{n}×',
    codeFormExpiry:   'Valid for',
    codeFormExpAny:   'Never expires',
    codeFormExp1d:    '1 day',
    codeFormExp7d:    '7 days',
    codeFormExp30d:   '30 days',
    codeFormSubmit:   'Generate code',
    codeEmpty:        'No invite code created yet.',

    inviteByEmailTitle: 'Invite by email',
    inviteByEmailSub:   "We'll send a nice email with a join link.",
    inviteEmailPh:      'name@company.com',
    inviteMessagePh:    'Personal message (optional)',
    inviteSend:         'Send invitation',
    inviteSentOk:       '✓ Invitation sent to {email}',

    settingsTitle:    'Organisation settings',
    settingsSub:      'Name, logo, deletion.',
    nameField:        'Organisation name',
    saveBtn:          'Save',
    dangerTitle:      '⚠ Delete organisation',
    dangerText:       'Permanently deletes the organisation. All files are archived, all chats and members removed. This action is final.',
    deleteBtn:        'Delete organisation',
    deleteConfirm:    'Really delete "{name}"? All data will be lost!',
    deletedOk:        'Organisation has been deleted.',

    memberSince:     'Member since {date}',

    joinTitle:        "You don't belong to an organisation yet",
    joinSub:          'Join an existing organisation or wait for an invitation.',
    joinCodePlaceholder: 'Enter invite code',
    joinPreview:      'Check code',
    joinConfirm:      'Join "{name}"',
    joinCancel:       'Cancel',
    joinPreviewLabel: 'If you join, you will belong to',
    joinOwnerLabel:   'Owner',
    joinMemberCount:  '{n} members',
    joinSuccess:      '✓ You are now a member of "{name}".',

    createTitle:      'Create your own organisation',
    createReady:      'Your license includes organisation creation. Pick a name and you\'re ready to invite your team.',
    createNeedsBiz:   'To create your own organisation you need a Business package (Basic or L, monthly or yearly), the All-in-One bundle or one of the org add-ons (Recruitment, MoreSpace). Unlock one in the shop to get: org management, time clock for everyone, payroll & time analysis, employee files and team chat.',
    createGoShop:     'Open shop',
    createOpenApp:    'Open the app',
    createNamePlaceholder: 'Organisation name',
    createBtn:        'Create organisation',
    createSuccess:    '✓ Your organisation "{name}" is ready.',
  },

  chat: {
    title:           'Chat',
    sub:             'Direct messages & group channels of your organisation.',
    listTitle:       'Conversations',
    empty:           'No chats yet. Start a direct message with an org member.',
    newDM:           'New message',
    newDMSelect:     'Pick a member:',
    backToList:      '← Back',
    placeholderInput: 'Write a message…',
    sendBtn:         'Send',
    typeDM:          'DM',
    typeGroup:       'Group',
    typeSupport:     'Support',
    nothingYet:      'Write the first message in this chat.',
    sentBy:          'You',
    today:           'Today',
    yesterday:       'Yesterday',
    unreadBadge:     '{n}',
    selectChat:      'Pick a chat from the list',
    selectChatSub:   'or start a new direct message with an org member.',
    deleteAction:    'Delete chat',
    deleteTitle:     'Delete chat?',
    deleteText:      '"{title}" will be removed from your list. Other members keep the chat — they can re-add you later.',

    lockedTitle:     'Chat is org-bound',
    lockedSub:       'You need an organisation to chat. Join one or wait for an invitation.',
    lockedJoin:      'Go to organisation →',

    // File attachment
    attachAction:        'Attach file',
    attachPlaceholder:   'Image, PDF or document…',
    attachUploading:     'Uploading…',
    attachRemove:        'Remove attachment',
    attachOpen:          'Open',
    attachDownload:      'Download',
    attachClose:         'Close',
    attachTooLarge:      'File is too large. Maximum {max} per attachment.',
    attachTypeBlocked:   'File type not allowed. Only images and documents (PDF, DOC, XLS, PPT, TXT…) are accepted.',
    attachUploadFailed:  'Upload failed: {error}',
  },

  payroll: {
    title:           'Time tracking & analysis',
    sub:             'Who worked how much — at a glance.',
    tabAnalysis:     'Time analysis',
    tabPayroll:      'Time tracking',
    tabEmployees:    'Employees',

    rangeWeek:       'This week',
    rangeMonth:      'This month',
    rangeQuarter:    'Quarter',
    rangeYear:       'Year',

    statTotal:       'Total work time',
    statEmployees:   'Employees',
    statAvgPerEmp:   'Avg per employee',
    statPauseTotal:  'Total breaks',
    statWorkDays:    'Work days',

    perMemberTitle:  'Per employee',
    perMemberEmpty:  'No data in this range.',
    memberDetailTitle: 'Time entries of {name}',
    memberDetailSub:   'Click an entry to edit it — every change is recorded in the audit log.',
    memberRecordsEmpty: 'No time entries in the last 30 days.',
    memberOpenRecords: 'View & edit entries',
    colName:         'Name',
    colDays:         'Days',
    colWorked:       'Worked',
    colPause:        'Break',
    colHourly:       'Hourly rate',
    colGross:        'Gross',
    colHours:        'Hours',
    colStatus:       'Status',
    statusLive:      'Live',
    statusOff:       'Off',

    payrollHint:     'Calculation: hours × hourly rate. Members without an hourly rate are shown as "—".',
    csvExport:       'CSV export',
    csvFile:         'payroll-{period}.csv',

    employeesEmpty:  'No employee profiles yet.',
    empSetSalary:    'Edit master data',
    empPosition:     'Position',
    empDepartment:   'Department',
    empWeeklyHours:  'Weekly hours',
    empHireDate:     'Hire date',
    empNotes:        'Notes (internal)',
    empSave:         'Save',
    empSaved:        '✓ Saved',
    empSalaryHidden: 'Salaries are only visible to the owner and members with "view salaries" permission.',

    locked:          'Time tracking is part of Business',
    lockedSub:       'Time tracking & analysis are automatically included in every Business package (Basic or L, monthly or yearly) — not a separate add-on. Unlock Business in the shop and it ships with it.',
    lockedShop:      'View Business in shop',
    forbidden:       'Not authorised',
    forbiddenSub:    'Only owner, admins or members with "view salaries" permission can see this.',
  },

  // ── Shift Planner ────────────────────────────────────────────────────────
  shifts: {
    kicker:        'NEW · WORKING HOURS',
    title:         'Shift Planner',
    sub:           'Weekly plan, assign people, auto-publish in chat — everyone sees their plan on every Apple device.',
    tabMy:         'My plan',
    tabManage:     'Manage shifts',

    myEmpty:       'No shifts scheduled yet.',
    myEmptySub:    'As soon as your team publishes the first plan, you\'ll see it here — and get a heads-up in the shift planner channel.',
    myUpcoming:    'Upcoming',
    myPast:        'Past',
    assigned:      'Scheduled',
    // Legacy (accept/decline workflow removed — members are only notified)
    accepted:      '✓ Accepted',
    declined:      '✗ Declined',
    pending:       '◔ Pending',
    accept:        'Accept',
    decline:       'Decline',
    declineReasonAsk: 'Reason (optional):',
    today:         'Today',
    tomorrow:      'Tomorrow',

    thisWeek:      'This week',
    add:           'Add shift',
    noPermTitle:   'You can\'t edit shifts',
    noPermSub:     'Ask your owner or an admin for the "manage shifts" permission.',

    newTitle:      'New shift',
    editTitle:     'Edit shift',
    fieldTitle:    'Title',
    fieldTitlePh:  'e.g. Morning shift bakery',
    fieldStart:    'Start',
    fieldEnd:      'End',
    fieldLocation: 'Location',
    fieldLocationPh: 'e.g. branch downtown',
    fieldRole:     'Role (optional)',
    fieldRolePh:   'e.g. cashier, service',
    fieldBreak:    'Break (minutes)',
    fieldColor:    'Color',
    fieldDesc:     'Description',
    fieldDescPh:   'What needs doing? Anything special? Hand-over notes?',
    fieldAssign:   'Assign people',
    noMembers:     'No members in the org.',
    publish:       'Publish & post in channel',
    deleteConfirm: 'Really delete this shift? All assignments will be removed.',

    upsellTitle:   'Shift Planner is an add-on',
    upsellSub:     'With the "Shift Planner" add-on (€9.99 one-time) you can create, assign and publish shifts for your team automatically in chat.',
    upsellCta:     'See in shop',

    noOrgTitle:    'Shifts are tied to your organisation',
    noOrgSub:      'You need an organisation to view or plan shifts.',
    noOrgCta:      'Go to organisation →',
  },

  punch: {
    title:           'Time clock',
    sub:             'Live timer with break tracking and weekly stats.',
    statusOut:       'Clocked out',
    statusIn:        'Clocked in',
    statusPaused:    'On break',
    clockIn:         '▶ Clock in',
    clockOut:        '■ Clock out',
    pauseStart:      '⏸ Break',
    pauseEnd:        '▶ Resume',
    sinceLabel:      'since {time}',
    todayWorked:     'Today',
    todayPaused:     'Break today',
    weekTotal:       'This week',
    weekAvg:         'Avg per day',
    notesLabel:      'Note',
    notesPh:         'Add a note (optional)…',
    saveNote:        'Save note',
    historyTitle:    'History',
    historyEmpty:    'No time entries yet.',
    historyToday:    'Today',
    historyYesterday:'Yesterday',
    deleteRecord:    'Delete entry',
    deleteConfirm:   'Really delete this entry?',
    durationFmt:     '{h}h {m}min',
    pauseFmt:        '{m}min break',
    runningFmt:      'running for {h}h {m}min',
    locked:          'Time clock not unlocked',
    lockedSub:       'You need the time-clock extension. Unlock it in the shop or with the Business package.',
    lockedShop:      'View in shop',
    rangeWeek:       'Week',
    rangeMonth:      'Month',
    rangeAll:        'All',
    pickRange:       'Range',

    resetTitle:      'Reset time clock',
    resetSub:        'Archive all your time entries as a personal profile — you start over from zero.',
    resetBtn:        'Reset time clock',
    resetConfirmTitle: 'Really reset?',
    resetConfirmText:  'All your time entries will be moved to an archive. You can still view them, but no longer edit. Your time clock then starts from zero.',
    resetConfirmDo:    'Reset',
    resetDoneTitle:    '✓ Reset',
    resetDoneN:        '{n} entries archived',
    resetBlockedOrg:   'In an organisation you cannot reset your time clock. Leave the organisation first — your entries will then be archived automatically.',

    archivesTitle:   'Archived time-clock profiles',
    archivesEmpty:   'No archived profiles yet.',
    archiveOpen:     'Open',
    archiveBack:     '← Back',
    archiveMeta:     'Archived on {date}',
    archiveReason: {
      user_reset:  'Self-reset',
      org_leave:   'When leaving the org',
      org_remove:  'Removed from org',
      org_delete:  'Org was disbanded',
    },
    archiveSpan:     '{from} – {to}',
    archiveTotal:    'Total worked',
    archiveEntries:  '{n} entries',
    archivePause:    'Total breaks',
    archiveReadOnly: 'These entries are archived — no longer editable.',

    editTitle:       'Edit time entry',
    editSub:         'Adjust start, end, break or note.',
    editClockIn:     'Start',
    editClockOut:    'End',
    editPauseMin:    'Break (minutes)',
    editNote:        'Note',
    editSave:        'Save',
    editCancel:      'Cancel',
    editSaved:       '✓ Saved',
    editEndBeforeStart: 'End cannot be before start.',
    editLockedNoPerm: 'In an organisation, time entries can only be edited with the "Manage employee profiles" permission.',
    editManualBadge: 'Manual',
    editBtn:         'Edit',
    deleteBtn:       'Delete',

    auditTitle:      'Change history',
    auditEmpty:      'No changes yet.',
    auditBy:         'by {editor}',
    auditWhen:       'on {date}',
    auditField: {
      clock_in:      'Start',
      clock_out:     'End',
      pause_seconds: 'Break',
      note:          'Note',
      is_manual:     'Manual flag',
    },
    auditFromTo:     'from {from} → {to}',
    auditUnset:      '(empty)',
  },

  recruit: {
    title:           'Recruitment manager',
    sub:             'Create job links, receive applications.',
    tabLinks:        'Jobs',
    tabApplications: 'Applications',

    linksEmpty:      'No job links yet.',
    newLink:         '+ New job',
    linkTitleField:  'Job title',
    linkTitlePh:     'e.g. "Bakery sales (part-time)"',
    linkDescField:   'Description',
    linkDescPh:      'Tasks, requirements, hours …',
    linkPositionField: 'Position (optional)',
    linkExpiry:      'Valid until',
    linkCreate:      'Create job',
    linkCreated:     '✓ Job link created',
    linkPublic:      'Public link',
    linkCopy:        'Copy',
    linkCopied:      '✓ Copied',
    linkActive:      'Active',
    linkInactive:    'Inactive',
    linkUsedCount:   '{n} application(s)',
    linkActions:     'Actions',
    linkEdit:        'Edit',
    linkDeactivate:  'Deactivate',
    linkActivate:    'Activate',
    linkDelete:      'Delete',
    linkDeleteConfirm: 'Really delete "{title}"? Existing applications will be kept.',

    appsEmpty:       'No applications yet.',
    appsAll:         'All',
    appsNew:         'New',
    appsViewed:      'Viewed',
    appsContacted:   'Contacted',
    appsAccepted:    'Accepted',
    appsRejected:    'Rejected',
    appName:         'Name',
    appEmail:        'Email',
    appPhone:        'Phone',
    appCoverLetter:  'Cover letter',
    appAttachments:  'Attachments',
    appNoAttach:     '(no attachments)',
    appNotes:        'Internal notes',
    appNotesPh:      'Internal notes for the team (only visible to you) …',
    appAssignTo:     'Assigned to',
    appNotAssigned:  '(no one)',
    appAppliedAt:    'Submitted on',
    appUpdate:       'Update',
    appUpdated:      '✓ Updated',
    appBack:         '← Back',

    locked:          'Recruitment not unlocked',
    lockedSub:       'You need the recruitment extension or a Business package. Unlock it in the shop.',
    lockedShop:      'View in shop',

    haveCodeTitle:   'Got an application code?',
    haveCodeSub:     'Enter it here to view the job posting and apply.',
    haveCodeBtn:     'View job →',
    haveCodeOpen:    'Open application page',
    recruiterHint:   'Want to receive applications yourself?',
    recruiterShop:   'Unlock in the shop →',
    recruiterOrg:    'Join or create an org →',

    applyKicker:     'Application',
    applyAt:         'at {org}',
    applyForm:       'Your application',
    applyFirstName:  'First name',
    applyLastName:   'Last name',
    applyEmail:      'Email',
    applyPhone:      'Phone (optional)',
    applyCover:      'Cover letter',
    applyCoverPh:    'Tell us briefly why this role excites you …',
    applyAttachTitle: 'Attachments (CV, certificates, …)',
    applyAttachHint: 'PDF, Word, image — up to 25 MB per file',
    applyDropHere:   'Drag files here or click',
    applyAttachOk:   '✓ Uploaded',
    applyAttachFail: '❌ Error',
    applyAttachLoad: '⏳ Uploading…',
    applySubmit:     'Send application',
    applySubmitting: 'Sending…',
    applyConsent:    'By submitting you confirm that your data will be stored to process this application. Privacy: ',
    applyConsentLink: 'privacy policy',
    applyDoneTitle:  'Thank you!',
    applyDoneText:   'Your application reached {org} — we will get back to you.',
    applyDoneAnother:'Another application',
    applyNotFound:   'Job listing not found',
    applyNotFoundSub:'This application code is invalid or the job is no longer active.',
    applyExpired:    'Job listing expired',
    applyExpiredSub: 'This application is no longer open.',
    applyCodeInputTitle: 'Enter application code',
    applyCodeInputSub: 'Got an application code from a company? Enter it here.',
    applyCodeInputPh: 'Code',
    applyCodeInputBtn: 'View job',
    applyCodeInputErr: 'Code unknown or job no longer active.',

    noOrgTitle:      'Recruitment is org-bound',
    noOrgSub:        'You need an organisation. Join one or create one.',
    noOrgGoOrg:      'Go to organisation →',
  },

  footer: {
    impressum:   'Imprint',
    datenschutz: 'Privacy',
    agb:         'Terms',
    contact:     'Contact',
    madeIn:      'Made with 🔥 in Germany',
  },
}
